import { useSoftDeployAccess, useSelector, useAppDispatch } from 'App/components/hooks';
import useComponentDidUnmount from 'App/components/hooks/useComponentDidUnmount';
import { CancelablePromise, makeCancelable } from 'Helpers/promise';
import { useTranslation } from 'Components/Helpers/translate';
import { type Metadata } from 'App/resources/AllIWant';
import { setMetadata } from 'Store/actions/AppActions';
import { MAX_CACHED_PRODUCTS } from 'Helpers/product';
import ClientParam from 'App/resources/ClientParam';
import { Flex, Help, Toggle, Text } from '@upsales/components';
import AdminHeader from './AdminHeader';
import logError from 'Helpers/logError';
import React, { useRef } from 'react';

interface AdminProductHeaderProps {
	tabs?: Array<{ id: string; title: string }>;
	selectedTab?: 'products' | 'categories';
	onTabChange?: (tab: string) => void;
}

const AdminProductHeader: React.FC<AdminProductHeaderProps> = ({ tabs, selectedTab, onTabChange }) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const hasOrderProductsImprovedSearch = useSoftDeployAccess('ORDER_PRODUCTS_IMPROVED_SEARCH');
	const hasCategoryAsDefaultTabFeature = useSoftDeployAccess('USE_CATEGORY_AS_DEFAULT_TAB_ON_ORDER');
	const hasNewResource = useSoftDeployAccess('NEW_CLIENT_PARAM_RESOURCE');
	const {
		totals: { products },
		metadata
	} = useSelector(({ App }) => App);

	const checked = metadata?.params.OrderProductsImprovedSearch;
	const useCategoryTabAsDefault = metadata?.params.EnableProductCategoryTabDefault;
	const showToggle = hasOrderProductsImprovedSearch && products <= MAX_CACHED_PRODUCTS;
	const toggleDisabled = products === 0;
	const clientParamReq = useRef<CancelablePromise | null>(null);
	const categoryDefaultParamReq = useRef<CancelablePromise | null>(null);

	useComponentDidUnmount(() => {
		clientParamReq.current?.cancel?.();
		categoryDefaultParamReq.current?.cancel?.();
	});

	const onToggleChange = (value: boolean) => {
		if (!hasNewResource) {
			Tools.ClientParam.save(261, value)
				.then(() => {
					const newMetaData = {
						...metadata,
						params: { ...metadata?.params, OrderProductsImprovedSearch: value }
					} as Metadata;

					dispatch(setMetadata(newMetaData));
				})
				.catch((error: any) => {
					logError(error);
				});
		} else {
			clientParamReq.current = makeCancelable(ClientParam.save({ id: 261, value }));
			clientParamReq.current.promise.catch(logError);
		}
	};

	const onCategoryDefaultToggleChange = (value: boolean) => {
		if (!hasNewResource) {
			Tools.ClientParam.save(269, value)
				.then(() => {
					const newMetaData = {
						...metadata,
						params: { ...metadata?.params, EnableProductCategoryTabDefault: value }
					} as Metadata;

					dispatch(setMetadata(newMetaData));
				})
				.catch((error: any) => {
					logError(error);
				});
		} else {
			categoryDefaultParamReq.current = makeCancelable(ClientParam.save({ id: 269, value }));
			categoryDefaultParamReq.current.promise.catch(logError);
		}
	};

	return (
		<>
			<AdminHeader
				title={t('default.products')}
				description={
					<div>
						{t('admin.products.description')} <Help sidebar articleId={1442} />
					</div>
				}
				image="products.svg"
				tabs={tabs}
				selectedTab={selectedTab}
				onTabChange={onTabChange}
				toggle={showToggle}
				toggleChecked={checked}
				toggleDisabled={toggleDisabled}
				onToggleChange={onToggleChange}
				toggleLabel={t('admin.products.header.toggleLabelImprovedSearch')}
			/>
			{hasCategoryAsDefaultTabFeature ? (
				<Flex alignItems="center" gap="12" spacing="ml6 pt5">
					<Toggle checked={useCategoryTabAsDefault} onChange={onCategoryDefaultToggleChange} />
					<Flex direction="column">
						<label>{t('admin.products.header.toggleLabelUseCategoryTabAsDefault')}</label>
						<Text color="grey-11" size="sm">
							{t('admin.products.header.toggleLabelUseCategoryTabAsDefaultDesc')}
						</Text>
					</Flex>
				</Flex>
			) : null}
		</>
	);
};

export default AdminProductHeader;
